import { forwardRef } from 'react';
import { shirtNameForRender, type ShirtPosition } from '@toca/core';

export interface ShirtPreviewProps {
  name: string;
  position: ShirtPosition;
  color: string;
  shirtColor?: string;
}

// Área de estampa (dentro do viewBox 400x480). Costas usa área maior; peito, menor.
const STAMP = {
  BACK: { x: 90, y: 150, w: 220, h: 90, fontBase: 46 },
  CHEST: { x: 250, y: 120, w: 110, h: 42, fontBase: 22 },
} as const;

/**
 * Preview vetorial da camisa UV. O nome é renderizado em maiúsculas e o tamanho da
 * fonte se ajusta ao comprimento; `textLength` + `lengthAdjust` GARANTEM que o texto
 * nunca ultrapassa a largura da área de estampa (mesmo com 18 caracteres).
 */
export const ShirtPreview = forwardRef<SVGSVGElement, ShirtPreviewProps>(function ShirtPreview(
  { name, position, color, shirtColor = '#1c6ea4' },
  ref,
) {
  const rendered = shirtNameForRender(name);
  const area = STAMP[position];
  const chars = Math.max(rendered.length, 1);

  // fonte proporcional ao comprimento (auto-shrink)
  const byLength = Math.floor((area.w * 1.7) / chars);
  const fontSize = Math.max(12, Math.min(area.fontBase, byLength));
  // largura estimada do texto; se exceder a área, força compressão via textLength
  const estWidth = rendered.length * fontSize * 0.62;
  const overflow = estWidth > area.w;
  const cx = area.x + area.w / 2;
  const cy = area.y + area.h / 2;

  return (
    <svg
      ref={ref}
      viewBox="0 0 400 480"
      width="100%"
      role="img"
      aria-label={rendered ? `Camisa com o nome ${rendered} (${position === 'BACK' ? 'costas' : 'peito'})` : 'Camisa sem nome'}
      xmlns="http://www.w3.org/2000/svg"
      style={{ maxWidth: 420, display: 'block' }}
    >
      <defs>
        <linearGradient id="tecido" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={shirtColor} />
          <stop offset="1" stopColor="#0f3d5c" />
        </linearGradient>
        <linearGradient id="fundo" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#241403" />
          <stop offset="1" stopColor="#140D08" />
        </linearGradient>
      </defs>

      <rect width="400" height="480" fill="url(#fundo)" rx="16" />

      {/* corpo da camisa (silhueta simples de camisa de pesca manga longa) */}
      <g>
        <path
          d="M140 70 L110 60 L60 110 L90 160 L120 145 L120 430 Q200 450 280 430 L280 145 L310 160 L340 110 L290 60 L260 70 Q200 105 140 70 Z"
          fill="url(#tecido)"
          stroke="#0a2c44"
          strokeWidth="3"
        />
        {/* gola */}
        <path d="M150 74 Q200 110 250 74 L250 92 Q200 126 150 92 Z" fill="#0a2c44" />
        {/* zíper */}
        <line x1="200" y1="92" x2="200" y2="150" stroke="#0a2c44" strokeWidth="3" />
      </g>

      {/* guia da área de estampa (sutil) */}
      <rect
        x={area.x}
        y={area.y}
        width={area.w}
        height={area.h}
        fill="none"
        stroke="rgba(255,215,0,.28)"
        strokeDasharray="5 5"
        rx="6"
      />

      {/* NOME aplicado */}
      {rendered && (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="'Nunito Sans', Arial, sans-serif"
          fontWeight={800}
          fontSize={fontSize}
          fill={color}
          {...(overflow ? { textLength: area.w - 8, lengthAdjust: 'spacingAndGlyphs' } : {})}
          style={{ letterSpacing: '1px', paintOrder: 'stroke', stroke: 'rgba(0,0,0,.35)', strokeWidth: 1 }}
        >
          {rendered}
        </text>
      )}
    </svg>
  );
});
